// @ts-nocheck
import {
  Box,
  Typography,
  IconButton,
  ButtonBase,
  Tooltip,
  Badge,
  useTheme,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import InfoIconButton from '../OtherDetails/InfoIconButton';
import PropTypes from 'prop-types';
import { ReactComponent as ModeEditOutlinedIcon } from '../../../../assets/Icons/lucideIcons/pencil.svg';
import { ReactComponent as ChatIcon } from '../../../../assets/Icons/lucideIcons/message-square-more.svg';
import { useSelector } from 'react-redux';
import { ReactComponent as HistoryIcon } from '../../../../assets/Icons/lucideIcons/history.svg';

import {
  checkAttributeIsRequired,
  formatAttributeValue,
  isValueEmpty,
} from '../../../../utils/Utils';
import useTextOverflow from '../../../../hooks/useTextOverFlow';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  NOT_AUTHORIZED_TO_ACCESS_MESSAGE,
  NOOP,
} from '../../../../const/CommonConst';
import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import OverrideRationaleTooltip from '../../../../components/Common/OverrideRationaleTooltip';
import {
  buildDraftChipTooltip,
  useShowDraftChip,
  useDraftChipMeta,
  useDraftAttributeValue,
} from '../../../../hooks/useDraftAutoSave.jsx';
import {
  DraftAttributeChip,
  DraftNewVariantFooter,
} from '../FieldComponents/DraftFieldExtras';

/**
 * Enhanced FieldInfo component with edit functionality
 * @param {Object} props - Component props
 * @param {string} props.label - The field label
 * @param {any} props.value - The field value
 * @param {Function} props.onEdit - Function to handle edit click
 * @param {Object} props.attribute - The attribute object with metadata
 * @returns {React.ReactElement} The field info component
 */
const FieldInfo = ({
  label,
  value,
  onEdit = NOOP,
  attribute,
  mode = 'edit',
  isNewVariant = false,
  isEditing = false,
  hasDraftChanges = false,
  isUserAuthorizedForUpdateAttribute,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  handleOpenDerivedModal = NOOP,
  overrideData,
  entityId = null,
  entityDetailsFormikForm = EMPTY_OBJECT,
  usersList = EMPTY_ARRAY,
  groupList = EMPTY_ARRAY,
  groupDetails = EMPTY_OBJECT,
  entityType = null,
  onHistoryClick = NOOP,
  associationTypes = EMPTY_OBJECT,
  showAttributeInfo = false,
  attributeSourceMapping = EMPTY_OBJECT,
  isSourceMappingLoading = false,
  entityData = EMPTY_OBJECT,
}) => {
  const showDraftChip = useShowDraftChip(attribute);
  const draftAttributeValue = useDraftAttributeValue(attribute?.id);
  const resolvedValue = useMemo(() => {
    if (showDraftChip && draftAttributeValue !== undefined) {
      return draftAttributeValue;
    }
    return value;
  }, [showDraftChip, draftAttributeValue, value]);
  const resolvedEntityId =
    entityId || entityData?.entity_id || entityData?.entityId;

  // Use the centralized utility function to format the value
  const displayValue = formatAttributeValue(
    resolvedValue,
    attribute,
    hasDraftChanges,
    resolvedEntityId,
    entityDetailsFormikForm,
    usersList,
    groupList,
    entityType,
    associationTypes,
    false,
    false,
    entityData,
    isNewVariant
  );
  const { ref: labelRef, isOverflowed: isLabelOverflowed } =
    useTextOverflow(label);

  const { ref: valueRef, isOverflowed: isValueOverflowed } =
    useTextOverflow(displayValue);
  const theme = useTheme();
  const labelColor = theme.palette.attributesForm?.label;
  const valueColor = theme.palette.attributesForm?.value;
  const { editorName: editorDisplayName = '', updatedAt: draftUpdatedAt } =
    useDraftChipMeta(attribute?.id);
  const draftChipTooltip = useMemo(
    () => buildDraftChipTooltip(editorDisplayName, draftUpdatedAt),
    [editorDisplayName, draftUpdatedAt]
  );
  const { frontendConfig, pluginConfig } = useSelector(
    (state) => state?.tenant
  );
  const { permissions } = useSelector((state) => state?.users || {});
  // Check if field is required based on yup_schema
  const isRequired = useMemo(() => {
    return checkAttributeIsRequired(attribute);
  }, [attribute]);
  const matchedOverride = useMemo(() => {
    return overrideData?.find?.((item) => item?.attributeId === attribute?.id);
  }, [overrideData, attribute?.id]);

  // Determine if the attribute is editable based on its properties
  // and whether it is excluded from editing
  const isEditable = useMemo(() => {
    return attribute?.is_editable && mode === 'edit';
  }, [attribute]);

  const isDerived = useMemo(() => {
    return attribute?.is_derived && mode === 'edit';
  }, [attribute]);

  const isEditEnabled = useMemo(() => {
    if (
      attribute?.name?.toLowerCase() === 'assigneduser' &&
      pluginConfig?.HDFCUAM &&
      !isNewVariant
    ) {
      return permissions?.is_super_admin || groupDetails?.isGroupAdmin;
    }
    if (attribute?.data_type === 'InventoryView') {
      return false; // InventoryView attributes are not editable in the single edit mode as per the current requirements
    }
    return isEditable && !isDerived && !frontendConfig?.hideSingleEditMode;
  }, [
    isEditable,
    isDerived,
    frontendConfig?.hideSingleEditMode,
    attribute?.name,
    permissions?.is_super_admin,
    groupDetails?.isGroupAdmin,
    pluginConfig?.HDFCUAM,
  ]);
  const isUserEditEnabled = useMemo(() => {
    if (
      attribute?.name?.toLowerCase() === 'assigneduser' &&
      pluginConfig?.HDFCUAM
    ) {
      return permissions?.is_super_admin || groupDetails?.isGroupAdmin;
    }
    return isUserAuthorizedForUpdateAttribute;
  }, [
    attribute?.name,
    permissions?.is_super_admin,
    groupDetails?.isGroupAdmin,
    pluginConfig?.HDFCUAM,
    isUserAuthorizedForUpdateAttribute,
  ]);

  // Create a memoized handler for the edit button click to avoid arrow function in JSX
  const handleEditClick = useCallback(() => {
    if (onEdit) {
      onEdit(attribute);
    }
  }, [onEdit, attribute]);

  const openChatBox = useCallback(() => {
    handleOpenChat(allAttributeChats?.[attribute?.name], attribute);
  }, [allAttributeChats, attribute?.name, handleOpenChat]);

  const handleClick = useCallback(() => {
    handleAttributeClick(attribute);
  }, [attribute, handleAttributeClick]);

  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  const showCalculationIcon = useMemo(() => {
    return (
      isDerived &&
      !isEditable &&
      attribute?.input_control_type !== 'SINGLE_SELECT' &&
      attribute?.data_type !== 'InventoryView' &&
      !frontendConfig?.hideSingleEditMode
    );
  }, [
    isDerived,
    isEditable,
    attribute?.input_control_type,
    frontendConfig?.hideSingleEditMode,
  ]);

  //show the history icon
  const showHistoryIcon = useMemo(() => {
    return attribute?.show_history;
  }, [attribute]);
  const handleHistoryClick = useCallback(
    (e) => {
      e.stopPropagation();
      onHistoryClick(attribute);
    },
    [onHistoryClick, attribute]
  );

  const showFullLabel = isNewVariant && (isEditing || mode === 'edit');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: isNewVariant ? 0 : 0.5,

        '&:hover .edit-icon-button': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      <Box
        display="flex"
        alignItems={showFullLabel ? 'flex-start' : 'center'}
        maxWidth="100%"
        minWidth={0}
        width="100%"
      >
        <Tooltip
          title={typeof label !== 'object' && isLabelOverflowed ? label : ''}
        >
          <Typography
            ref={labelRef}
            component={showFullLabel ? 'div' : 'span'}
            variant="attributeLabel"
            sx={{
              color: labelColor,
              ...(showFullLabel
                ? {
                    display: 'block',
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }
                : {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flexShrink: 1,
                  }),
              ':hover': {
                fontWeight: 600,
                cursor: 'pointer',
              },
            }}
            onClick={handleClick}
          >
            {label}
            {showFullLabel && isRequired && (
              <Typography
                component="span"
                variant="body8"
                color="error.main"
                sx={{ marginLeft: '2px', lineHeight: 1 }}
              >
                *
              </Typography>
            )}
          </Typography>
        </Tooltip>

        {!showFullLabel && isRequired && (
          <Typography
            component="span"
            variant="body8"
            sx={{
              color: 'error.main',
              marginLeft: '2px',
              flexShrink: 0,
              lineHeight: 1,
            }}
          >
            *
          </Typography>
        )}

        {showDraftChip && (
          <DraftAttributeChip
            isNewVariant={isNewVariant}
            draftChipTooltip={draftChipTooltip}
          />
        )}

        {showHistoryIcon && (
          <Tooltip title="History">
            <ButtonBase
              onClick={handleHistoryClick}
              aria-label={`View history for ${label}`}
              sx={{ color: 'primary.main', flexShrink: 0, ml: 1 }}
            >
              <HistoryIcon width={16} height={16} />
            </ButtonBase>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '36px',
        }}
      >
        <Tooltip
          title={
            typeof displayValue !== 'object' && isValueOverflowed
              ? displayValue
              : ''
          }
        >
          <Typography
            ref={valueRef}
            variant="attributeValue"
            sx={{
              color: valueColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}
          >
            {displayValue}
            {matchedOverride?.justification && (
              <OverrideRationaleTooltip
                justification={matchedOverride?.justification}
              />
            )}
          </Typography>
        </Tooltip>
        {isEditEnabled && (
          <IconButton
            className="edit-icon-button"
            onClick={handleEditClick}
            size="small"
            color="primary"
            title={
              isUserEditEnabled ? 'Edit' : NOT_AUTHORIZED_TO_ACCESS_MESSAGE
            }
            aria-label={
              isUserEditEnabled ? 'Edit' : NOT_AUTHORIZED_TO_ACCESS_MESSAGE
            }
            disabled={!isUserEditEnabled}
            sx={{
              flexShrink: 0,
              opacity: 0,
              visibility: 'hidden',
              transition:
                'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            }}
          >
            <ModeEditOutlinedIcon />
          </IconButton>
        )}
        {showCalculationIcon && (
          <IconButton
            className="edit-icon-button disabled-svg-icon"
            onClick={handleClickAttribute}
            size="small"
            color="primary"
            title={
              !attribute?.show_calculation_popUp
                ? 'Enable the calculation popup to calculate this attribute.'
                : `Calculate ${attribute?.label}`
            }
            disabled={!attribute?.show_calculation_popUp}
            aria-label="Calculate Derived Attribute"
            sx={{
              flexShrink: 0,
              opacity: 0,
              visibility: 'hidden',
              transition:
                'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            }}
          >
            <CalculationIcon />
          </IconButton>
        )}

        {!isValueEmpty(allAttributeChats?.[attribute?.name]) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={openChatBox}
          >
            <Badge
              badgeContent={allAttributeChats?.[attribute?.name]?.length}
              sx={{
                '& .MuiBadge-badge': {
                  marginRight: '0px',
                  marginTop: '0px',
                  right: '-4px',
                },
              }}
            >
              <ChatIcon color="primary" width={18} height={18} />
            </Badge>
          </Box>
        )}

        {showAttributeInfo && (
          <InfoIconButton
            attribute={attribute}
            attributeSourceMapping={attributeSourceMapping}
            loading={isSourceMappingLoading}
          />
        )}
      </Box>
      {isNewVariant && showDraftChip && (
        <DraftNewVariantFooter
          editorName={editorDisplayName}
          updatedAt={draftUpdatedAt}
        />
      )}
    </Box>
  );
};

FieldInfo.propTypes = {
  isNewVariant: PropTypes.bool,
  isEditing: PropTypes.bool,
  label: PropTypes.string.isRequired,
  hasDraftChanges: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  onEdit: PropTypes.func,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
    description: PropTypes.string,
    is_editable: PropTypes.bool,
    is_derived: PropTypes.bool,
    show_history: PropTypes.bool,
    show_calculation_popUp: PropTypes.bool,
    is_inventory_view_attribute_linked: PropTypes.bool,
  }),
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  mode: PropTypes.oneOf(['edit', 'view']),
  isUserAuthorizedForUpdateAttribute: PropTypes.bool.isRequired,
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)])
    .isRequired,
  handleOpenChat: PropTypes.func.isRequired,
  handleAttributeClick: PropTypes.func.isRequired,
  handleOpenDerivedModal: PropTypes.func.isRequired,
  overrideData: PropTypes.oneOf([PropTypes.object]).isRequired,
  entityId: PropTypes.number,
  entityDetailsFormikForm: PropTypes.oneOf([PropTypes.object]).isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  entityType: PropTypes.string,
  onHistoryClick: PropTypes.func,
  showAttributeInfo: PropTypes.bool,
  attributeSourceMapping: PropTypes.objectOf(PropTypes.shape({})),
  isSourceMappingLoading: PropTypes.bool,
  associationTypes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        entity_name: PropTypes.string,
        entity_type: PropTypes.string,
        template_id: PropTypes.string,
        version_id: PropTypes.string,
      })
    )
  ),
  entityData: PropTypes.shape({}),
};

export default React.memo(FieldInfo);
